<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { AdminEventConfig } from '../../lib/convexApi';

  type EventExtraForm = {
    sku: string;
    name: string;
    description: string;
    price: string;
    currency: string;
    capacity: string;
    capacityUnlimited: boolean;
    maxPerOrder: string;
  };

  type EventForm = {
    id: string;
    name: string;
    headline: string;
    description: string;
    locationName: string;
    locationAddress: string;
    locationMapUrl: string;
    whatsappGroupUrl: string;
    applicationUrl: string;
    scheduleStart: string;
    scheduleEnd: string;
    ticketProductName: string;
    ticketCurrency: string;
    ticketStartPrice: string;
    ticketEndPrice: string;
    ticketDynamicStart: string;
    ticketDynamicEnd: string;
    ticketAllowInvites: boolean;
    ticketDefaultInviteAllowance: string;
    ticketQuestion: string;
    extras: EventExtraForm[];
  };

  export let initial: AdminEventConfig | null = null;
  export let saving = false;
  export let error: string | null = null;

  const dispatch = createEventDispatcher();

  const emptyForm = (): EventForm => ({
    id: '',
    name: '',
    headline: '',
    description: '',
    locationName: '',
    locationAddress: '',
    locationMapUrl: '',
    whatsappGroupUrl: '',
    applicationUrl: '',
    scheduleStart: '',
    scheduleEnd: '',
    ticketProductName: '',
    ticketCurrency: 'eur',
    ticketStartPrice: '',
    ticketEndPrice: '',
    ticketDynamicStart: '',
    ticketDynamicEnd: '',
    ticketAllowInvites: false,
    ticketDefaultInviteAllowance: '1',
    ticketQuestion: '',
    extras: []
  });

  let form: EventForm = emptyForm();
  let lastInitialKey: string | null = null;

  function applyInitial(config: AdminEventConfig | null) {
    if (!config) {
      form = emptyForm();
      return;
    }
    form = {
      id: config.id,
      name: config.name,
      headline: config.headline,
      description: config.description ?? '',
      locationName: config.location.name,
      locationAddress: config.location.address ?? '',
      locationMapUrl: config.location.mapUrl ?? '',
      whatsappGroupUrl: config.whatsappGroupUrl ?? '',
      applicationUrl: config.applicationUrl ?? '',
      scheduleStart: config.schedule.start,
      scheduleEnd: config.schedule.end,
      ticketProductName: config.ticket.productName,
      ticketCurrency: config.ticket.currency,
      ticketStartPrice: String(config.ticket.startPrice),
      ticketEndPrice: String(config.ticket.endPrice),
      ticketDynamicStart: config.ticket.dynamicPricing?.start ?? '',
      ticketDynamicEnd: config.ticket.dynamicPricing?.end ?? '',
      ticketAllowInvites: Boolean(config.ticket.allowInvites),
      ticketDefaultInviteAllowance: String(
        config.ticket.defaultInviteAllowance && Number.isFinite(config.ticket.defaultInviteAllowance)
          ? Math.max(1, Number(config.ticket.defaultInviteAllowance))
          : 1
      ),
      ticketQuestion: config.ticket.preCheckoutQuestion ?? '',
      extras: (config.extras ?? []).map(extra => ({
        sku: extra.sku,
        name: extra.name,
        description: extra.description ?? '',
        price: String(extra.price),
        currency: extra.currency,
        capacity: extra.capacity < 0 ? '' : String(extra.capacity),
        capacityUnlimited: extra.capacity < 0,
        maxPerOrder: String(extra.maxPerOrder)
      }))
    };
  }

  $: {
    const key = initial ? `${initial.id}-${JSON.stringify({
      schedule: initial.schedule,
      ticket: initial.ticket,
      extras: initial.extras,
      description: initial.description ?? null,
      whatsappGroupUrl: initial.whatsappGroupUrl ?? null,
      applicationUrl: initial.applicationUrl ?? null
    })}` : null;
    if (key !== lastInitialKey) {
      applyInitial(initial);
      lastInitialKey = key;
    }
  }

  function addExtraRow() {
    form = {
      ...form,
      extras: [
        ...form.extras,
        {
          sku: '',
          name: '',
          description: '',
          price: '',
          currency: 'eur',
          capacity: '',
          capacityUnlimited: false,
          maxPerOrder: ''
        }
      ]
    };
  }

  function updateExtra(index: number, key: keyof EventExtraForm, value: string | boolean) {
    const next = form.extras.slice();
    const current = next[index];
    if (!current) return;
    next[index] = {
      ...current,
      [key]: value
    } as EventExtraForm;
    form = { ...form, extras: next };
  }

  function onTextInput(index: number, key: keyof EventExtraForm, event: Event) {
    const target = event.currentTarget as HTMLInputElement | null;
    updateExtra(index, key, target?.value ?? '');
  }

  function onCheckboxChange(index: number, key: keyof EventExtraForm, event: Event) {
    const target = event.currentTarget as HTMLInputElement | null;
    updateExtra(index, key, Boolean(target?.checked));
  }

  function removeExtraRow(index: number) {
    const next = form.extras.slice();
    next.splice(index, 1);
    form = { ...form, extras: next };
  }

  function buildPayload(): AdminEventConfig {
    const startPrice = Number(form.ticketStartPrice);
    const endPrice = Number(form.ticketEndPrice);

    return {
      id: form.id.trim(),
      name: form.name.trim(),
      headline: form.headline.trim(),
      location: {
        name: form.locationName.trim(),
        address: form.locationAddress.trim() || null,
        mapUrl: form.locationMapUrl.trim() || null
      },
      description: (() => {
        const trimmed = form.description.trim();
        return trimmed.length > 0 ? trimmed : null;
      })(),
      whatsappGroupUrl: form.whatsappGroupUrl.trim() || null,
      applicationUrl: form.applicationUrl.trim() || null,
      schedule: {
        start: form.scheduleStart,
        end: form.scheduleEnd
      },
      ticket: {
        productName: form.ticketProductName.trim(),
        currency: form.ticketCurrency.trim() || 'eur',
        startPrice: Number.isFinite(startPrice) ? startPrice : 0,
        endPrice: Number.isFinite(endPrice) ? endPrice : startPrice,
        dynamicPricing:
          form.ticketDynamicStart || form.ticketDynamicEnd
            ? {
                start: form.ticketDynamicStart || null,
                end: form.ticketDynamicEnd || null
            }
            : null,
        allowInvites: form.ticketAllowInvites,
        defaultInviteAllowance: form.ticketAllowInvites
          ? Math.max(1, Number.parseInt(form.ticketDefaultInviteAllowance || '1', 10) || 1)
          : undefined,
        preCheckoutQuestion: (() => {
          const trimmed = form.ticketQuestion.trim();
          return trimmed.length > 0 ? trimmed : null;
        })()
      },
      extras: form.extras
        .filter(extra => extra.sku.trim() && extra.name.trim())
        .map(extra => ({
          sku: extra.sku.trim(),
          name: extra.name.trim(),
          description: extra.description.trim() || null,
          price: Number(extra.price) || 0,
          currency: extra.currency.trim() || 'eur',
          capacity: extra.capacityUnlimited ? -1 : Number(extra.capacity) || 0,
          maxPerOrder: Number(extra.maxPerOrder) || 1
        }))
    };
  }

  function submit(event: Event) {
    event.preventDefault();
    dispatch('save', { event: buildPayload() });
  }

  function cancel() {
    dispatch('cancel');
  }
</script>

<form class="event-editor" on:submit|preventDefault={submit}>
  <header>
    <div>
      <h2>{initial ? 'Edit event' : 'Create event'}</h2>
      <p>Define the core information and product settings for your experience.</p>
    </div>
    <div class="actions">
      <button type="button" class="ghost" on:click={cancel} disabled={saving}>Cancel</button>
      <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save event'}</button>
    </div>
  </header>

  {#if error}
    <div class="alert">{error}</div>
  {/if}

  <section class="grid">
    <label>
      <span>Event ID</span>
      <input bind:value={form.id} required placeholder="slug (e.g., midnight-auction)" />
    </label>
    <label>
      <span>Event name</span>
      <input bind:value={form.name} required />
    </label>
    <label>
      <span>Headline</span>
      <input bind:value={form.headline} />
    </label>
  </section>

  <section class="grid description-field">
    <label>
      <span>Description</span>
      <textarea
        bind:value={form.description}
        maxlength="2500"
        rows="5"
        placeholder="Share what makes this event special."
      ></textarea>
      <small class="field-hint">Supports Markdown or HTML · {form.description.length}/2500 characters</small>
    </label>
  </section>

  <section class="grid">
    <label>
      <span>Location name</span>
      <input bind:value={form.locationName} />
    </label>
    <label>
      <span>Location address</span>
      <input bind:value={form.locationAddress} />
    </label>
    <label>
      <span>Map URL</span>
      <input bind:value={form.locationMapUrl} />
    </label>
    <label>
      <span>WhatsApp group link</span>
      <input
        type="url"
        bind:value={form.whatsappGroupUrl}
        placeholder="https://chat.whatsapp.com/..."
      />
      <small class="field-hint">Leave blank to hide the WhatsApp CTA.</small>
    </label>
    <label>
      <span>Application URL</span>
      <input
        type="url"
        bind:value={form.applicationUrl}
        placeholder="https://forms.example.com/apply"
      />
      <small class="field-hint">Optional. Opens in a new tab when guests click Apply.</small>
    </label>
  </section>

  <section class="grid">
    <label>
      <span>Starts</span>
      <input type="datetime-local" bind:value={form.scheduleStart} required />
    </label>
    <label>
      <span>Ends</span>
      <input type="datetime-local" bind:value={form.scheduleEnd} required />
    </label>
    <div class="ticket-preview">
      <span>Ticket product</span>
      <div class="ticket-preview-card">
        <strong>{form.ticketProductName || 'Product Name'}</strong>
        <small>{form.ticketCurrency.toUpperCase()}</small>
      </div>
    </div>
  </section>

  <section class="grid">
    <label>
      <span>Ticket product name</span>
      <input bind:value={form.ticketProductName} required />
    </label>
    <label>
      <span>Ticket currency</span>
      <input bind:value={form.ticketCurrency} required />
    </label>
    <label>
      <span>Price range</span>
      <div class="price-range">
        <input type="number" min="0" step="0.01" bind:value={form.ticketStartPrice} placeholder="Start" />
        <span>→</span>
        <input type="number" min="0" step="0.01" bind:value={form.ticketEndPrice} placeholder="End" />
      </div>
    </label>
  </section>

  <section class="grid">
    <label>
      <span>Dynamic start</span>
      <input type="datetime-local" bind:value={form.ticketDynamicStart} />
    </label>
    <label>
      <span>Dynamic end</span>
      <input type="datetime-local" bind:value={form.ticketDynamicEnd} />
    </label>
  </section>

  <section class="grid invite-settings">
    <label class="toggle">
      <input type="checkbox" bind:checked={form.ticketAllowInvites} />
      <span>Allow guests to generate plus-one invites</span>
    </label>
    <label class="allowance" class:disabled={!form.ticketAllowInvites}>
      <span>Default guest allowance</span>
      <input
        type="number"
        min="1"
        step="1"
        bind:value={form.ticketDefaultInviteAllowance}
        disabled={!form.ticketAllowInvites}
      />
      <small>Hosts can raise this per invite. Minimum of 1.</small>
    </label>
  </section>

  <section class="grid ticket-question">
    <label>
      <span>Ticket question</span>
      <textarea
        bind:value={form.ticketQuestion}
        maxlength="2500"
        rows="4"
        placeholder="e.g., What brings you to this event?"
      ></textarea>
      <small class="field-hint">{form.ticketQuestion.length}/2500 characters</small>
    </label>
  </section>

  <section class="extras">
    <header>
      <h3>Extras</h3>
      <button type="button" class="ghost" on:click={addExtraRow}>Add extra</button>
    </header>

    {#if form.extras.length === 0}
      <p class="muted">No extras yet. Add upgrades, tables, or add-ons here.</p>
    {:else}
      <div class="extras-grid">
        {#each form.extras as extra, index}
          <div class="extras-card">
            <div class="extras-row">
              <label>
                <span>SKU</span>
                <input value={extra.sku} on:input={(event) => onTextInput(index, 'sku', event)} />
              </label>
              <label>
                <span>Name</span>
                <input value={extra.name} on:input={(event) => onTextInput(index, 'name', event)} />
              </label>
            </div>
            <div class="extras-row">
              <label>
                <span>Description</span>
                <input value={extra.description} on:input={(event) => onTextInput(index, 'description', event)} />
              </label>
            </div>
            <div class="extras-row">
              <label>
                <span>Price</span>
                <input type="number" min="0" step="0.01" value={extra.price} on:input={(event) => onTextInput(index, 'price', event)} />
              </label>
              <label>
                <span>Currency</span>
                <input value={extra.currency} on:input={(event) => onTextInput(index, 'currency', event)} />
              </label>
            </div>
            <div class="extras-row">
              <label class="capacity">
                <span>Capacity</span>
                <input
                  type="number"
                  min="0"
                  value={extra.capacity}
                  disabled={extra.capacityUnlimited}
                  on:input={(event) => onTextInput(index, 'capacity', event)}
                />
              </label>
              <label class="toggle">
                <input
                  type="checkbox"
                  checked={extra.capacityUnlimited}
                  on:change={(event) => onCheckboxChange(index, 'capacityUnlimited', event)}
                />
                <span>Unlimited</span>
              </label>
              <label>
                <span>Per guest max</span>
                <input
                  type="number"
                  min="1"
                  value={extra.maxPerOrder}
                  on:input={(event) => onTextInput(index, 'maxPerOrder', event)}
                />
              </label>
            </div>
            <div class="extras-actions">
              <button type="button" class="ghost" on:click={() => removeExtraRow(index)}>Remove</button>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </section>
</form>

<style>
  .event-editor {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-5);
    border-radius: var(--radius-xl);
    background: linear-gradient(180deg, rgba(14, 16, 24, 0.92), rgba(14, 16, 24, 0.85));
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: var(--shadow-lg);
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-4);
  }

  header h2 {
    margin: 0;
    font-size: 1.4rem;
  }

  .actions {
    display: flex;
    gap: var(--space-3);
  }

  button {
    border: none;
    padding: 0.65rem 1.1rem;
    border-radius: var(--radius-lg);
    cursor: pointer;
    font-weight: 600;
  }

  button[type='submit'] {
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.75), rgba(178, 127, 244, 0.55));
    color: #050608;
  }

  button.ghost {
    background: rgba(255, 255, 255, 0.08);
    color: var(--color-text-primary);
  }

  .alert {
    padding: var(--space-3);
    border-radius: var(--radius-md);
    background: rgba(255, 96, 96, 0.12);
    border: 1px solid rgba(255, 96, 96, 0.4);
  }

  .grid {
    display: grid;
    gap: var(--space-4);
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }

  .ticket-question {
    grid-template-columns: 1fr;
  }

  .description-field {
    grid-template-columns: 1fr;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.9rem;
  }

  .field-hint {
    font-size: 0.75rem;
    color: rgba(244, 241, 232, 0.6);
  }

  input {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    padding: 0.6rem 0.75rem;
    color: inherit;
  }

  textarea {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    padding: 0.75rem;
    color: inherit;
    resize: vertical;
    min-height: 120px;
  }

  input:focus {
    outline: none;
    border-color: rgba(240, 169, 74, 0.6);
    box-shadow: 0 0 0 1px rgba(240, 169, 74, 0.4);
  }

  .price-range {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .price-range input {
    width: 100%;
  }

  .ticket-preview {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .ticket-preview-card {
    padding: 0.75rem;
    border-radius: var(--radius-lg);
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .invite-settings {
    align-items: flex-start;
  }

  .invite-settings .toggle {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-weight: 600;
  }

  .invite-settings .toggle input {
    width: auto;
  }

  .allowance {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .allowance input {
    max-width: 6rem;
  }

  .allowance small {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.75rem;
  }

  .allowance.disabled {
    opacity: 0.6;
  }

  .extras {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .extras header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .extras-grid {
    display: grid;
    gap: var(--space-4);
  }

  .extras-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
    background: rgba(255, 255, 255, 0.06);
  }

  .extras-row {
    display: grid;
    gap: var(--space-3);
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }

  .extras-actions {
    display: flex;
    justify-content: flex-end;
  }

  .toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .muted {
    color: var(--color-text-muted);
  }
</style>
